import * as React from "react";
import { useState, useEffect } from "react";
import axios from "axios";

import {
  Button,
  CircularProgress,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Chip,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Box,
  Avatar,
  Tooltip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import RefreshIcon from "@material-ui/icons/Refresh";

import { Notification, useNotify } from "react-admin";
import { API } from "../Constant";
import { getConfigGet, getConfigPatch } from "../getConfig";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    padding: theme.spacing(3),
    backgroundColor: theme.palette.type === "dark" ? "#1a1a1a" : "#f5f5f5",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  title: {
    fontWeight: 600,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  filterContainer: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    alignItems: "flex-end",
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  filterField: {
    minWidth: 180,
  },
  tableContainer: {
    borderRadius: 12,
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },
  tableHead: {
    backgroundColor: theme.palette.type === "dark" ? theme.palette.grey[800] : "#fafafa",
  },
  tableHeadCell: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  brandCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  brandLogo: {
    width: 40,
    height: 40,
    objectFit: "contain",
    borderRadius: 4,
    backgroundColor: "#fff",
    border: "1px solid #e0e0e0",
  },
  brandName: {
    fontWeight: 500,
  },
  overrideActive: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    fontWeight: 500,
  },
  overrideInactive: {
    backgroundColor: theme.palette.type === "dark" ? "#2a2a2a" : "#fafafa",
    color: "#9e9e9e",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "50vh",
  },
  dialogContent: {
    padding: theme.spacing(2),
    minWidth: 550,
  },
  dialogField: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    color: theme.palette.text.primary,
  },
  referenceSection: {
    backgroundColor: theme.palette.type === "dark" ? "#2a2a2a" : "#f5f5f5",
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  referenceTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: 12,
    textTransform: "uppercase",
  },
  referenceItem: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(0.5, 0),
    fontSize: 13,
  },
  metadataContainer: {
    display: "flex",
    gap: theme.spacing(3),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  metadataCard: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    padding: theme.spacing(2),
    minWidth: 150,
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  },
  metadataValue: {
    fontSize: 24,
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  metadataLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
  },
  tabPanel: {
    padding: theme.spacing(2, 0),
  },
  accordionRoot: {
    marginBottom: theme.spacing(1),
    "&:before": {
      display: "none",
    },
    borderRadius: "8px !important",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  accordionSummary: {
    backgroundColor: theme.palette.type === "dark" ? "#2a2a2a" : "#fafafa",
    borderRadius: 8,
  },
  jsonField: {
    fontFamily: "monospace",
    fontSize: 12,
  },
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

interface Brand {
  id: number;
  brand: string;
  brand_name: string;
  logo_url: string | null;
  status: string;
  discount: number | null;
  sprive_haircut_percentage: number | null;
  effective_discount: number | null;
  effective_sprive_haircut: number | null;
  overall_discount: number | null;
  boosted_discount: number | null;
  effective_boost_haircut: number | null;
  boost_haircut_source: "brand_override" | "global_default" | "fallback";
  boost_haircut_override_percentage: number | null;
  has_mapping: boolean;
  mapping_id: number | null;
  brand_name_override: string | null;
  brand_logo_override: string | null;
  discount_override_percentage: number | null;
  discount_override_from_date: string | null;
  discount_override_to_date: string | null;
  sprive_haircut_override_percentage: number | null;
  sprive_haircut_override_from_date: string | null;
  sprive_haircut_override_to_date: string | null;
  recurring_purchase_subscription: boolean;
}

interface BrandDetails extends Brand {
  sprive_terms_and_condition_override: string | null;
  return_policy: string | null;
  redemption_methods_override: any | null;
  redemption_instruction: any | null;
  exclusions: any | null;
  categories_override: any | null;
  categories: any | null;
  description: string | null;
  expiry: string | null;
  digital_face_value_limits_lower: number | null;
  digital_face_value_limits_upper: number | null;
  terms_and_conditions_url: string | null;
  redemption_instructions_url: string | null;
  website_url: string | null;
  mapping_created_at: string | null;
  mapping_updated_at: string | null;
}

interface Pagination {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
  has_next_page: boolean;
  has_previous_page: boolean;
}

interface BrandMetadata {
  total_brands: number;
  enabled_count: number;
  boost_formula_note?: string;
  as_of_date?: string;
  preview_mode?: boolean;
}

interface EditFormData {
  brand_name_override: string;
  brand_logo_override: string;
  discount_override_percentage: string;
  discount_override_from_date: string;
  discount_override_to_date: string;
  sprive_haircut_override_percentage: string;
  sprive_haircut_override_from_date: string;
  sprive_haircut_override_to_date: string;
  boost_haircut_override_percentage: string;
  sprive_terms_and_condition_override: string;
  return_policy: string;
  redemption_methods_override: string;
  redemption_instruction: string;
  exclusions: string;
  categories_override: string;
  recurring_purchase_subscription: boolean;
}

const BRAND_MANAGEMENT_ENDPOINT = API + "/admin/gift-card-brand-management";

const GiftCardBrandManagement = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    page_size: 10,
    total: 0,
    total_pages: 0,
    has_next_page: false,
    has_previous_page: false,
  });
  const [metadata, setMetadata] = useState<BrandMetadata>({
    total_brands: 0,
    enabled_count: 0,
    boost_formula_note: "",
  });

  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("brand_name");
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("ASC");
  const [hasMapping, setHasMapping] = useState<string>("");
  const [previewDate, setPreviewDate] = useState<string>(""); // YYYY-MM-DD

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<BrandDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [editTabValue, setEditTabValue] = useState(0);
  const [editFormData, setEditFormData] = useState<EditFormData>({
    brand_name_override: "",
    brand_logo_override: "",
    discount_override_percentage: "",
    discount_override_from_date: "",
    discount_override_to_date: "",
    sprive_haircut_override_percentage: "",
    sprive_haircut_override_from_date: "",
    sprive_haircut_override_to_date: "",
    boost_haircut_override_percentage: "",
    sprive_terms_and_condition_override: "",
    return_policy: "",
    redemption_methods_override: "",
    redemption_instruction: "",
    exclusions: "",
    categories_override: "",
    recurring_purchase_subscription: false,
  });

  useEffect(() => {
    fetchBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.page_size, sortBy, sortOrder]);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: pagination.page,
        pageSize: pagination.page_size,
        sortBy,
        sortOrder,
      };

      if (search) params.search = search;
      if (hasMapping) params.has_mapping = hasMapping;
      if (previewDate) params.as_of_date = previewDate;

      const config = getConfigGet(params);
      const response: any = await axios.get(BRAND_MANAGEMENT_ENDPOINT, config);

      if (response.data.success) {
        setBrands(response.data.data);
        setPagination(response.data.pagination);
        setMetadata(response.data.metadata);
      }
    } catch (error: any) {
      notify(
        error?.response?.data?.message || "Failed to load brands",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchBrandDetails = async (brandId: number) => {
    try {
      setLoadingDetails(true);
      const detailParams: any = {};
      if (previewDate) detailParams.as_of_date = previewDate;
      const config = getConfigGet(detailParams);
      const response: any = await axios.get(
        `${BRAND_MANAGEMENT_ENDPOINT}/${brandId}`,
        config
      );

      if (response.data.success) {
        const brand = response.data.data;
        setSelectedBrand(brand);
        setEditFormData({
          brand_name_override: brand.brand_name_override || "",
          brand_logo_override: brand.brand_logo_override || "",
          discount_override_percentage:
            brand.discount_override_percentage?.toString() || "",
          discount_override_from_date:
            brand.discount_override_from_date?.split("T")[0] || "",
          discount_override_to_date:
            brand.discount_override_to_date?.split("T")[0] || "",
          sprive_haircut_override_percentage:
            brand.sprive_haircut_override_percentage?.toString() || "",
          sprive_haircut_override_from_date:
            brand.sprive_haircut_override_from_date?.split("T")[0] || "",
          sprive_haircut_override_to_date:
            brand.sprive_haircut_override_to_date?.split("T")[0] || "",
          boost_haircut_override_percentage:
            brand.boost_haircut_override_percentage?.toString() || "",
          sprive_terms_and_condition_override:
            brand.sprive_terms_and_condition_override || "",
          return_policy: brand.return_policy || "",
          redemption_methods_override: brand.redemption_methods_override
            ? JSON.stringify(brand.redemption_methods_override, null, 2)
            : "",
          redemption_instruction: brand.redemption_instruction
            ? JSON.stringify(brand.redemption_instruction, null, 2)
            : "",
          exclusions: brand.exclusions
            ? JSON.stringify(brand.exclusions, null, 2)
            : "",
          categories_override: brand.categories_override
            ? JSON.stringify(brand.categories_override, null, 2)
            : "",
          recurring_purchase_subscription:
            brand.recurring_purchase_subscription || false,
        });
      }
    } catch (error: any) {
      notify(
        error?.response?.data?.message || "Failed to load brand details",
        "error"
      );
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSearch = () => {
    setPagination({ ...pagination, page: 1 });
    fetchBrands();
  };

  const handleClearFilters = () => {
    setSearch("");
    setSortBy("brand_name");
    setSortOrder("ASC");
    setHasMapping("");
    setPreviewDate("");
    setPagination({ ...pagination, page: 1 });
    setTimeout(() => fetchBrands(), 100);
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPagination({ ...pagination, page: newPage + 1 });
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setPagination({
      ...pagination,
      page_size: parseInt(event.target.value, 10),
      page: 1,
    });
  };

  const handleEditClick = (brand: Brand) => {
    setEditTabValue(0);
    setEditDialogOpen(true);
    fetchBrandDetails(brand.id);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSelectedBrand(null);
    setEditTabValue(0);
  };

  const validateJsonField = (value: string, fieldName: string): boolean => {
    if (!value || value.trim() === "") return true;
    try {
      JSON.parse(value);
      return true;
    } catch {
      notify(`${fieldName} must be valid JSON`, "error");
      return false;
    }
  };

  const handleSave = async () => {
    if (!selectedBrand) return;

    if (
      !validateJsonField(
        editFormData.redemption_methods_override,
        "Redemption Methods"
      ) ||
      !validateJsonField(
        editFormData.redemption_instruction,
        "Redemption Instruction"
      ) ||
      !validateJsonField(editFormData.exclusions, "Exclusions") ||
      !validateJsonField(editFormData.categories_override, "Categories Override")
    ) {
      return;
    }

    setSaving(true);
    try {
      const payload: any = {};

      if (editFormData.brand_name_override) {
        payload.brand_name_override = editFormData.brand_name_override;
      }
      if (editFormData.brand_logo_override) {
        payload.brand_logo_override = editFormData.brand_logo_override;
      }

      payload.discount_override_percentage =
        editFormData.discount_override_percentage !== ""
          ? parseFloat(editFormData.discount_override_percentage)
          : null;
      payload.discount_override_from_date =
        editFormData.discount_override_from_date || null;
      payload.discount_override_to_date =
        editFormData.discount_override_to_date || null;

      payload.sprive_haircut_override_percentage =
        editFormData.sprive_haircut_override_percentage !== ""
          ? parseFloat(editFormData.sprive_haircut_override_percentage)
          : null;
      payload.sprive_haircut_override_from_date =
        editFormData.sprive_haircut_override_from_date || null;
      payload.sprive_haircut_override_to_date =
        editFormData.sprive_haircut_override_to_date || null;

      payload.boost_haircut_override_percentage =
        editFormData.boost_haircut_override_percentage !== ""
          ? parseFloat(editFormData.boost_haircut_override_percentage)
          : null;

      if (editFormData.sprive_terms_and_condition_override) {
        payload.sprive_terms_and_condition_override =
          editFormData.sprive_terms_and_condition_override;
      }
      if (editFormData.return_policy) {
        payload.return_policy = editFormData.return_policy;
      }

      if (editFormData.redemption_methods_override) {
        payload.redemption_methods_override = JSON.parse(
          editFormData.redemption_methods_override
        );
      }
      if (editFormData.redemption_instruction) {
        payload.redemption_instruction = JSON.parse(
          editFormData.redemption_instruction
        );
      }
      if (editFormData.exclusions) {
        payload.exclusions = JSON.parse(editFormData.exclusions);
      }
      if (editFormData.categories_override) {
        payload.categories_override = JSON.parse(
          editFormData.categories_override
        );
      }

      payload.recurring_purchase_subscription =
        editFormData.recurring_purchase_subscription;

      const headers = getConfigPatch();
      const response: any = await axios.patch(
        `${BRAND_MANAGEMENT_ENDPOINT}/${selectedBrand.id}`,
        payload,
        { headers }
      );

      if (response.data.success) {
        notify(response.data.message, "info");
        handleEditClose();
        fetchBrands();
      }
    } catch (error: any) {
      notify(
        error?.response?.data?.message || "Failed to update brand",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const formatPercentage = (value: number | null) => {
    if (value === null || value === undefined) return "-";
    return `${value}%`;
  };

  const getRefDate = () =>
    previewDate ? new Date(`${previewDate}T12:00:00`) : new Date();

  const isDiscountOverrideActive = (brand: Brand) => {
    if (brand.discount_override_percentage === null) return false;
    const ref = getRefDate();
    const from = brand.discount_override_from_date
      ? new Date(brand.discount_override_from_date)
      : null;
    const to = brand.discount_override_to_date
      ? new Date(brand.discount_override_to_date)
      : null;
    if (from && ref < from) return false;
    if (to && ref > to) return false;
    return true;
  };

  const isHaircutOverrideActive = (brand: Brand) => {
    if (brand.sprive_haircut_override_percentage === null) return false;
    const ref = getRefDate();
    const from = brand.sprive_haircut_override_from_date
      ? new Date(brand.sprive_haircut_override_from_date)
      : null;
    const to = brand.sprive_haircut_override_to_date
      ? new Date(brand.sprive_haircut_override_to_date)
      : null;
    if (from && ref < from) return false;
    if (to && ref > to) return false;
    return true;
  };

  if (loading && brands.length === 0) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress size={40} />
      </div>
    );
  }

  return (
    <div className={classes.main}>
      <div className={classes.header}>
        <div>
          <Typography variant="h5" className={classes.title}>
            Gift Card Brand Management
          </Typography>
          <Typography variant="body2" className={classes.subtitle}>
            Manage brand overrides, discounts, haircuts, and content
          </Typography>
        </div>
      </div>

      <div className={classes.metadataContainer}>
        <div className={classes.metadataCard}>
          <div className={classes.metadataValue}>{metadata.total_brands}</div>
          <div className={classes.metadataLabel}>Total Brands</div>
        </div>
        <div className={classes.metadataCard}>
          <div className={classes.metadataValue}>{metadata.enabled_count}</div>
          <div className={classes.metadataLabel}>Enabled</div>
        </div>
      </div>

      <Paper
        style={{
          padding: 12,
          marginBottom: 16,
          borderRadius: 8,
          background: "#f7f9fc",
        }}
      >
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          Boosted % explanation
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Overall Discount is the cashback users get right now. Boosted % is the cashback users get when Boost is active. If a brand has a Boost haircut set, that is used first; otherwise the default Boost haircut from the active campaign is used.
        </Typography>
      </Paper>

      <div className={classes.filterContainer}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={classes.filterField}
          placeholder="Search by brand name..."
          onKeyPress={(e) => e.key === "Enter" && handleSearch()}
        />

        <FormControl
          variant="outlined"
          size="small"
          className={classes.filterField}
        >
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as string)}
            label="Sort By"
          >
            <MenuItem value="brand_name">Brand Name</MenuItem>
            <MenuItem value="discount">Discount</MenuItem>
            <MenuItem value="sprive_haircut_percentage">Haircut</MenuItem>
          </Select>
        </FormControl>

        <FormControl
          variant="outlined"
          size="small"
          className={classes.filterField}
        >
          <InputLabel>Sort Order</InputLabel>
          <Select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as "ASC" | "DESC")}
            label="Sort Order"
          >
            <MenuItem value="ASC">Ascending</MenuItem>
            <MenuItem value="DESC">Descending</MenuItem>
          </Select>
        </FormControl>

        <FormControl
          variant="outlined"
          size="small"
          className={classes.filterField}
        >
          <InputLabel>Has Mapping</InputLabel>
          <Select
            value={hasMapping}
            onChange={(e) => setHasMapping(e.target.value as string)}
            label="Has Mapping"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="contained"
          color="primary"
          onClick={handleSearch}
          disabled={loading}
        >
          Search
        </Button>

        <Button variant="outlined" onClick={handleClearFilters}>
          Clear
        </Button>

        <IconButton onClick={fetchBrands} disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </div>

      <Paper
        style={{
          padding: "12px 16px",
          marginBottom: 16,
          borderRadius: 8,
          backgroundColor: previewDate ? "#fff8f0" : "#fafafa",
          border: `1px solid ${previewDate ? "#ffb74d" : "#e0e0e0"}`,
          display: "flex",
          alignItems: "center",
          gap: 16,
          transition: "background-color 0.2s, border-color 0.2s",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Typography
            variant="caption"
            style={{
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: previewDate ? "#e65100" : "#9e9e9e",
              marginBottom: 4,
            }}
          >
            Date Preview
          </Typography>
          <Typography variant="caption" style={{ color: "#757575" }}>
            See how discounts &amp; haircuts look on a specific date (past or future)
          </Typography>
        </div>
        <TextField
          variant="outlined"
          size="small"
          type="date"
          value={previewDate}
          onChange={(e) => setPreviewDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          style={{ minWidth: 180 }}
          inputProps={{
            style: { color: previewDate ? "#e65100" : undefined, fontWeight: previewDate ? 600 : undefined },
          }}
        />
        <Button
          variant={previewDate ? "contained" : "outlined"}
          size="small"
          disabled={!previewDate || loading}
          onClick={handleSearch}
          style={
            previewDate
              ? { backgroundColor: "#e65100", color: "#fff", minWidth: 110 }
              : { minWidth: 110 }
          }
        >
          Apply Preview
        </Button>
        {previewDate && (
          <Button
            size="small"
            variant="text"
            style={{ color: "#9e9e9e" }}
            onClick={() => { setPreviewDate(""); setTimeout(() => fetchBrands(), 50); }}
          >
            Clear Date
          </Button>
        )}
        {previewDate && (
          <Typography
            variant="body2"
            style={{
              marginLeft: "auto",
              color: "#e65100",
              fontWeight: 600,
              backgroundColor: "#ffe0b2",
              borderRadius: 20,
              padding: "4px 12px",
              whiteSpace: "nowrap",
            }}
          >
            Previewing: {new Date(`${previewDate}T12:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
          </Typography>
        )}
      </Paper>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell className={classes.tableHeadCell}>Brand</TableCell>
              <TableCell className={classes.tableHeadCell}>
                Base Discount
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                Effective Discount
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                Base Haircut
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                Effective Haircut
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                Overall Discount
              </TableCell>
              <TableCell className={classes.tableHeadCell}>
                <Tooltip
                  title="Boosted % shows customer cashback when boost is active."
                  placement="top"
                >
                  <span>Boosted %</span>
                </Tooltip>
              </TableCell>
              <TableCell className={classes.tableHeadCell} align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {brands.map((brand) => (
              <TableRow key={brand.id} hover>
                <TableCell>
                  <div className={classes.brandCell}>
                    <Avatar
                      src={brand.brand_logo_override || brand.logo_url || ""}
                      alt={brand.brand_name}
                      className={classes.brandLogo}
                      variant="square"
                    />
                    <div>
                      <div className={classes.brandName}>
                        {brand.brand_name_override || brand.brand_name}
                      </div>
                      {brand.brand_name_override && (
                        <div style={{ fontSize: 11, color: "#999" }}>
                          ({brand.brand_name})
                        </div>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell>{formatPercentage(brand.discount)}</TableCell>
                <TableCell>
                  <Chip
                    label={formatPercentage(brand.effective_discount)}
                    size="small"
                    className={
                      isDiscountOverrideActive(brand)
                        ? classes.overrideActive
                        : classes.overrideInactive
                    }
                  />
                </TableCell>
                <TableCell>
                  {formatPercentage(brand.sprive_haircut_percentage)}
                </TableCell>
                <TableCell>
                  <Chip
                    label={formatPercentage(brand.effective_sprive_haircut)}
                    size="small"
                    className={
                      isHaircutOverrideActive(brand)
                        ? classes.overrideActive
                        : classes.overrideInactive
                    }
                  />
                </TableCell>
                <TableCell>
                  {brand.overall_discount !== null
                    ? formatPercentage(brand.overall_discount)
                    : brand.effective_discount !== null &&
                      brand.effective_sprive_haircut !== null
                    ? formatPercentage(
                        brand.effective_discount - brand.effective_sprive_haircut
                      )
                    : "-"}
                </TableCell>
                <TableCell>
                  {brand.boosted_discount !== null
                    ? formatPercentage(brand.boosted_discount)
                    : brand.effective_discount !== null &&
                      brand.boost_haircut_override_percentage !== null
                    ? formatPercentage(
                        brand.effective_discount -
                          brand.boost_haircut_override_percentage
                      )
                    : "-"}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Edit Brand">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEditClick(brand)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <TablePagination
          count={pagination.total}
          page={pagination.page - 1}
          onChangePage={handlePageChange}
          rowsPerPage={pagination.page_size}
          onChangeRowsPerPage={handleRowsPerPageChange}
          rowsPerPageOptions={[10]}
        />
      </TableContainer>

      <Dialog
        open={editDialogOpen}
        onClose={handleEditClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {selectedBrand && (
              <Avatar
                src={
                  selectedBrand.brand_logo_override ||
                  selectedBrand.logo_url ||
                  ""
                }
                variant="square"
                style={{ width: 32, height: 32 }}
              />
            )}
            <div>
              Edit Brand Configuration
              {selectedBrand && (
                <Typography variant="body2" color="textSecondary">
                  {selectedBrand.brand_name_override || selectedBrand.brand_name}
                </Typography>
              )}
            </div>
          </div>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {loadingDetails ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: 40,
              }}
            >
              <CircularProgress />
            </div>
          ) : selectedBrand ? (
            <>
              {previewDate && (
                <div
                  style={{
                    backgroundColor: "#fff3e0",
                    border: "1px solid #ffb74d",
                    borderRadius: 6,
                    padding: "8px 12px",
                    marginBottom: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span role="img" aria-label="calendar" style={{ fontSize: 14 }}>🗓️</span>
                  <Typography variant="caption" style={{ color: "#e65100", fontWeight: 600 }}>
                    Preview Mode — effective values shown as of {new Date(`${previewDate}T12:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                  </Typography>
                </div>
              )}
              <div className={classes.referenceSection}>
                <div className={classes.referenceTitle}>
                  Base Values (Read-only)
                </div>
                <div className={classes.referenceItem}>
                  <span>Base Discount:</span>
                  <span>{formatPercentage(selectedBrand.discount)}</span>
                </div>
                <div className={classes.referenceItem}>
                  <span>Base Haircut:</span>
                  <span>
                    {formatPercentage(selectedBrand.sprive_haircut_percentage)}
                  </span>
                </div>
                <div className={classes.referenceItem}>
                  <span>Status:</span>
                  <span>{selectedBrand.status}</span>
                </div>
                {selectedBrand.mapping_updated_at && (
                  <div className={classes.referenceItem}>
                    <span>Last Updated:</span>
                    <span>
                      {new Date(
                        selectedBrand.mapping_updated_at
                      ).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>

              <Tabs
                value={editTabValue}
                onChange={(_e, v) => setEditTabValue(v)}
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab label="Overrides" />
                <Tab label="Content" />
                <Tab label="Settings" />
              </Tabs>

              <TabPanel value={editTabValue} index={0}>
                <div className={classes.tabPanel}>
                  <Typography variant="subtitle2" className={classes.sectionTitle}>
                    Brand Identity Override
                  </Typography>
                  <TextField
                    className={classes.dialogField}
                    label="Brand Name Override"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={editFormData.brand_name_override}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        brand_name_override: e.target.value,
                      })
                    }
                    helperText="Override the display name for this brand"
                  />
                  <TextField
                    className={classes.dialogField}
                    label="Brand Logo URL Override"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={editFormData.brand_logo_override}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        brand_logo_override: e.target.value,
                      })
                    }
                    helperText="Override the logo URL for this brand"
                  />

                  <Divider style={{ margin: "16px 0" }} />

                  <Typography variant="subtitle2" className={classes.sectionTitle}>
                    Discount Override
                  </Typography>
                  <TextField
                    className={classes.dialogField}
                    label="Discount Override Percentage"
                    variant="outlined"
                    fullWidth
                    size="small"
                    type="number"
                    value={editFormData.discount_override_percentage}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        discount_override_percentage: e.target.value,
                      })
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                    helperText="Leave empty to use base discount"
                  />
                  <div style={{ display: "flex", gap: 16 }}>
                    <TextField
                      className={classes.dialogField}
                      label="From Date"
                      variant="outlined"
                      fullWidth
                      size="small"
                      type="date"
                      value={editFormData.discount_override_from_date}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          discount_override_from_date: e.target.value,
                        })
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      className={classes.dialogField}
                      label="To Date"
                      variant="outlined"
                      fullWidth
                      size="small"
                      type="date"
                      value={editFormData.discount_override_to_date}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          discount_override_to_date: e.target.value,
                        })
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </div>

                  <Divider style={{ margin: "16px 0" }} />

                  <Typography variant="subtitle2" className={classes.sectionTitle}>
                    Sprive Haircut Override
                  </Typography>
                  <TextField
                    className={classes.dialogField}
                    label="Haircut Override Percentage"
                    variant="outlined"
                    fullWidth
                    size="small"
                    type="number"
                    value={editFormData.sprive_haircut_override_percentage}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        sprive_haircut_override_percentage: e.target.value,
                      })
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                    helperText="Leave empty to use base haircut"
                  />
                  <div style={{ display: "flex", gap: 16 }}>
                    <TextField
                      className={classes.dialogField}
                      label="From Date"
                      variant="outlined"
                      fullWidth
                      size="small"
                      type="date"
                      value={editFormData.sprive_haircut_override_from_date}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          sprive_haircut_override_from_date: e.target.value,
                        })
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      className={classes.dialogField}
                      label="To Date"
                      variant="outlined"
                      fullWidth
                      size="small"
                      type="date"
                      value={editFormData.sprive_haircut_override_to_date}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          sprive_haircut_override_to_date: e.target.value,
                        })
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </div>

                  <Divider style={{ margin: "16px 0" }} />

                  <Typography variant="subtitle2" className={classes.sectionTitle}>
                    Boost Haircut Override
                  </Typography>
                  <TextField
                    className={classes.dialogField}
                    label="Boost Haircut Override Percentage"
                    variant="outlined"
                    fullWidth
                    size="small"
                    type="number"
                    value={editFormData.boost_haircut_override_percentage}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        boost_haircut_override_percentage: e.target.value,
                      })
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                    helperText="Haircut applied when user has active boost. Leave empty for global boost haircut."
                  />
                </div>
              </TabPanel>

              <TabPanel value={editTabValue} index={1}>
                <div className={classes.tabPanel}>
                  <Typography variant="subtitle2" className={classes.sectionTitle}>
                    Terms & Policies
                  </Typography>
                  <TextField
                    className={classes.dialogField}
                    label="Terms & Conditions Override"
                    variant="outlined"
                    fullWidth
                    size="small"
                    multiline
                    rows={3}
                    value={editFormData.sprive_terms_and_condition_override}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        sprive_terms_and_condition_override: e.target.value,
                      })
                    }
                  />
                  <TextField
                    className={classes.dialogField}
                    label="Return Policy"
                    variant="outlined"
                    fullWidth
                    size="small"
                    multiline
                    rows={3}
                    value={editFormData.return_policy}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        return_policy: e.target.value,
                      })
                    }
                  />

                  <Divider style={{ margin: "16px 0" }} />

                  <Typography variant="subtitle2" className={classes.sectionTitle}>
                    JSON Fields
                  </Typography>
                  <Accordion className={classes.accordionRoot}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      className={classes.accordionSummary}
                    >
                      <Typography>Redemption Methods Override</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TextField
                        fullWidth
                        multiline
                        rows={5}
                        variant="outlined"
                        size="small"
                        value={editFormData.redemption_methods_override}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            redemption_methods_override: e.target.value,
                          })
                        }
                        className={classes.jsonField}
                        placeholder='{"method": "value"}'
                      />
                    </AccordionDetails>
                  </Accordion>

                  <Accordion className={classes.accordionRoot}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      className={classes.accordionSummary}
                    >
                      <Typography>Redemption Instruction</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TextField
                        fullWidth
                        multiline
                        rows={5}
                        variant="outlined"
                        size="small"
                        value={editFormData.redemption_instruction}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            redemption_instruction: e.target.value,
                          })
                        }
                        className={classes.jsonField}
                        placeholder='["step1", "step2"]'
                      />
                    </AccordionDetails>
                  </Accordion>

                  <Accordion className={classes.accordionRoot}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      className={classes.accordionSummary}
                    >
                      <Typography>Exclusions</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TextField
                        fullWidth
                        multiline
                        rows={5}
                        variant="outlined"
                        size="small"
                        value={editFormData.exclusions}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            exclusions: e.target.value,
                          })
                        }
                        className={classes.jsonField}
                        placeholder='["exclusion1", "exclusion2"]'
                      />
                    </AccordionDetails>
                  </Accordion>

                  <Accordion className={classes.accordionRoot}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      className={classes.accordionSummary}
                    >
                      <Typography>Categories Override</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TextField
                        fullWidth
                        multiline
                        rows={5}
                        variant="outlined"
                        size="small"
                        value={editFormData.categories_override}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            categories_override: e.target.value,
                          })
                        }
                        className={classes.jsonField}
                        placeholder='["category1", "category2"]'
                      />
                    </AccordionDetails>
                  </Accordion>
                </div>
              </TabPanel>

              <TabPanel value={editTabValue} index={2}>
                <div className={classes.tabPanel}>
                  <Typography variant="subtitle2" className={classes.sectionTitle}>
                    Brand Settings
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editFormData.recurring_purchase_subscription}
                        onChange={(e) =>
                          setEditFormData({
                            ...editFormData,
                            recurring_purchase_subscription: e.target.checked,
                          })
                        }
                        color="primary"
                      />
                    }
                    label="Enable Recurring Purchase Subscription"
                  />
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ marginTop: 8 }}
                  >
                    Allow users to set up recurring purchases for this brand
                  </Typography>

                  {selectedBrand.description && (
                    <>
                      <Divider style={{ margin: "24px 0" }} />
                      <Typography
                        variant="subtitle2"
                        className={classes.sectionTitle}
                      >
                        Brand Information (Read-only)
                      </Typography>
                      <div className={classes.referenceSection}>
                        <div className={classes.referenceItem}>
                          <span>Description:</span>
                        </div>
                        <Typography variant="body2" style={{ marginTop: 4 }}>
                          {selectedBrand.description}
                        </Typography>
                        {selectedBrand.expiry && (
                          <div
                            className={classes.referenceItem}
                            style={{ marginTop: 12 }}
                          >
                            <span>Expiry:</span>
                            <span>{selectedBrand.expiry}</span>
                          </div>
                        )}
                        {selectedBrand.digital_face_value_limits_lower && (
                          <div className={classes.referenceItem}>
                            <span>Face Value Range:</span>
                            <span>
                              £{selectedBrand.digital_face_value_limits_lower} -
                              £{selectedBrand.digital_face_value_limits_upper}
                            </span>
                          </div>
                        )}
                        {selectedBrand.website_url && (
                          <div className={classes.referenceItem}>
                            <span>Website:</span>
                            <a
                              href={selectedBrand.website_url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {selectedBrand.website_url}
                            </a>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </TabPanel>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose} color="default">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={saving || loadingDetails}
          >
            {saving && (
              <CircularProgress size={16} style={{ marginRight: 8 }} />
            )}
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Notification />
    </div>
  );
};

export default GiftCardBrandManagement;
